import React from 'react';
import { Shield, FileText, Heart, Users, Lock, Clock, ArrowRight, CheckCircle, Gift } from 'lucide-react';
import { useWallet } from '../Context/WalletContext';

export default function LandingPage({ onGetStarted, onBeneficiaryLogin }) {
  const { connected, connectWallet } = useWallet();

  const features = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Secure & Decentralized",
      description: "Your will is stored on the blockchain, ensuring it's tamper-proof and always accessible."
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Heartbeat Monitoring",
      description: "Regular check-ins prove you're alive. If you miss one, your will is automatically released."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Multi-Beneficiary Support",
      description: "Designate multiple beneficiaries with customizable quorum requirements."
    },
    {
      icon: <Lock className="w-8 h-8" />,
      title: "Encrypted Storage",
      description: "Documents are encrypted and stored on IPFS for maximum privacy and availability."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Time-Based Release",
      description: "Set custom intervals for heartbeat checks based on your preferences."
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Digital Documents",
      description: "Upload and store all your important documents securely in one place."
    }
  ];

  const howItWorks = [
    {
      step: "1",
      title: "Connect Wallet",
      description: "Connect your MetaMask wallet to get started with WillGuard."
    },
    {
      step: "2",
      title: "Upload Documents",
      description: "Upload your will and important documents to IPFS storage."
    },
    {
      step: "3",
      title: "Set Parameters",
      description: "Configure heartbeat interval, beneficiaries, and quorum requirements."
    },
    {
      step: "4",
      title: "Send Heartbeats",
      description: "Regularly check in to prove you're alive and keep your will secure."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 backdrop-blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg">
                <Shield className="w-16 h-16 text-white" />
              </div>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600">
              WillGuard
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-4 max-w-3xl mx-auto">
              Decentralized Digital Will Registry
            </p>
            
            <p className="text-lg text-gray-500 mb-8 max-w-2xl mx-auto">
              Secure your legacy with blockchain technology. Ensure your wishes are honored
              with our tamper-proof, decentralized will management system.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              {!connected ? (
                <>
                  <button
                    onClick={connectWallet}
                    className="group px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                  >
                    Connect Wallet
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button
                    onClick={connectWallet}
                    className="group px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                  >
                    <Gift className="w-5 h-5" />
                    Login as Beneficiary
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={onGetStarted}
                    className="group px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                  >
                    Go to Dashboard
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button
                    onClick={onBeneficiaryLogin}
                    className="group px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                  >
                    <Gift className="w-5 h-5" />
                    Beneficiary Portal
                  </button>
                </>
              )}
              
              <button
                onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-white text-gray-700 rounded-xl font-semibold text-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 border-2 border-gray-200"
              >
                Learn More
              </button>
            </div>

            {connected && (
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-full">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-green-800 font-medium">Wallet Connected</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Why Choose WillGuard?
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Built on blockchain technology to provide unmatched security and reliability
            for your most important documents.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group p-6 bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-indigo-200"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-600">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* How It Works Section */}
      <div id="how-it-works" className="bg-gradient-to-r from-indigo-50 to-purple-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Get started with WillGuard in four simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-600">
                    {item.description}
                  </p>
                </div>
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-indigo-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl shadow-2xl overflow-hidden">
          <div className="px-8 py-16 text-center">
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to Secure Your Legacy?
            </h2>
            <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
              Join thousands who trust WillGuard to protect their digital inheritance
            </p>
            {!connected ? (
              <>
                <button
                  onClick={connectWallet}
                  className="px-8 py-4 bg-white text-indigo-600 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Get Started Now
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onGetStarted}
                  className="px-8 py-4 bg-white text-indigo-600 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 mr-4"
                >
                  Go to Dashboard
                </button>
                <button
                  onClick={onBeneficiaryLogin}
                  className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Beneficiary Portal
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-gray-600">
            <p className="flex items-center justify-center gap-2">
              <Shield className="w-5 h-5" />
              <span>© 2025 WillGuard. Securing your digital legacy on the blockchain.</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
